import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@safetyhub/ui';
import { useAuthStore } from '../../stores/auth';
import { useToastStore } from '../../stores/toast';
import { extractApiError } from '../../lib/api';

/**
 * 탈퇴 완료 통지의 **복원 링크** 랜딩 (S27 예외 ③).
 *
 * 로그인 흐름의 복원(`restorePending` → `RestoreConfirmModal`)과 목적은 같지만 화면을 따로 둔다 —
 * 링크로 들어온 사람은 로그인 상태가 아니라 뒤에 깔릴 맥락이 없고, 이 화면이 그 사람이 보는
 * 유일한 설명이기 때문이다.
 *
 * 검증은 서버가 한다(`POST /auth/restore`가 유예·사유·탈퇴 회차를 전부 다시 본다). 여기서
 * `exp`를 읽는 것은 **남은 기간 표시용**뿐이라, 못 읽어도 화면이 성립해야 한다.
 */
function readExpiry(token: string): Date | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number };
    return payload && json.exp ? new Date(json.exp * 1000) : null;
  } catch {
    return null;
  }
}

export default function AccountRestore() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { restore } = useAuthStore();
  const { showToast } = useToastStore();

  const token = searchParams.get('token') ?? '';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const expiresAt = useMemo(() => (token ? readExpiry(token) : null), [token]);
  const daysLeft = useMemo(() => {
    if (!expiresAt) return null;
    const ms = expiresAt.getTime() - Date.now();
    return ms > 0 ? Math.ceil(ms / (1000 * 60 * 60 * 24)) : 0;
  }, [expiresAt]);

  // 링크 자체가 없는 접근은 안내할 것이 없다
  useEffect(() => {
    if (!token) setError('복원 링크가 올바르지 않습니다.');
  }, [token]);

  const handleRestore = async () => {
    setLoading(true);
    setError(null);
    try {
      await restore(token);
      showToast('계정이 복원되었습니다. 다시 오신 것을 환영합니다!', 'success');
      navigate('/', { replace: true });
    } catch (err) {
      // ⚠️ `err.message`를 쓰면 안 된다 — store가 axios 에러를 그대로 throw하고 AxiosError는
      // Error라서, 사용자에게 "Request failed with status code 410"이 그대로 보였다.
      // 서버가 준 한국어 사유(만료·무효·이미 복원됨)를 쓴다.
      setError(
        extractApiError(err).message ||
          '계정 복원에 실패했습니다. 링크가 만료되었을 수 있습니다.',
      );
      setLoading(false);
    }
  };

  const expired = daysLeft === 0;

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold text-brand-600 hover:text-brand-700">SafetyHub</h1>
          </Link>

          <h2 className="mt-8 text-2xl font-bold text-ink-900">계정 복원</h2>

          {expired ? (
            <p className="mt-4 text-ink-600">
              복원 기한이 지났습니다. 이 링크로는 계정을 되돌릴 수 없습니다.
            </p>
          ) : (
            <>
              <p className="mt-4 text-ink-600">
                탈퇴한 계정을 다시 사용하시겠습니까?
              </p>
              {daysLeft !== null && (
                <p className="mt-2 text-sm text-ink-500">
                  복원 가능 기간이 <strong className="text-ink-700">{daysLeft}일</strong> 남았습니다.
                  기한이 지나면 계정과 자료가 모두 삭제됩니다.
                </p>
              )}
            </>
          )}

          {error && (
            <p className="mt-4 text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <div className="mt-8 space-y-3">
            {!expired && (
              <Button
                type="button"
                onClick={handleRestore}
                disabled={loading || !token}
                className="w-full"
              >
                {loading ? '복원 중...' : '계정 복원하기'}
              </Button>
            )}
            <Link
              to="/login"
              className="block w-full py-2 px-4 border border-ink-300 rounded-control text-sm font-medium text-ink-700 hover:bg-ink-100"
            >
              로그인 화면으로
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
